import { useEffect, useState } from "react";
import { apiFetch } from "../utils/api";

const ROLE_FIELDS = [
  { key: "host", label: "主持", headerBg: "bg-emerald-200", cellBg: "bg-emerald-50" },
  { key: "singer", label: "唱歌", headerBg: "bg-sky-200", cellBg: "bg-sky-50" },
  { key: "projector", label: "放映", headerBg: "bg-emerald-200", cellBg: "bg-emerald-50" },
];

export default function BookClubDutySection({ current }) {
  const userId = localStorage.getItem("user_id");

  const [weeks, setWeeks] = useState([]);
  const [activeWeekId, setActiveWeekId] = useState(null);
  const [weekDetail, setWeekDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [message, setMessage] = useState("");

  const loadWeeks = async () => {
    if (!current?.id) return;
    setLoading(true);
    try {
      const res = await apiFetch(`/api/public/book-club/duty-weeks?reading_id=${current.id}`);
      const data = await res.json();
      if (res.ok) {
        const items = data.items || [];
        setWeeks(items);
        if (items.length) {
          const prefer =
            items.find((w) => String(w.id) === String(activeWeekId)) ||
            items[items.length - 1];
          setActiveWeekId(prefer.id);
        } else {
          setActiveWeekId(null);
          setWeekDetail(null);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const loadWeekDetail = async (weekId) => {
    if (!weekId) return;
    const res = await apiFetch(`/api/public/book-club/duty-weeks/${weekId}`);
    const data = await res.json();
    if (res.ok) setWeekDetail(data);
  };

  useEffect(() => {
    loadWeeks();
  }, [current?.id]);

  useEffect(() => {
    if (activeWeekId) loadWeekDetail(activeWeekId);
  }, [activeWeekId]);

  const updateDayField = (dayId, field, value) => {
    setWeekDetail((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        days: (prev.days || []).map((d) =>
          d.id === dayId ? { ...d, [field]: value } : d
        ),
      };
    });
  };

  const saveDay = async (day) => {
    setSavingId(day.id);
    setMessage("");
    try {
      const body = {
        host: day.host,
        singer: day.singer,
        projector: day.projector,
        reading_content: day.reading_content,
      };
      if (userId) body.user_id = Number(userId);

      const res = await apiFetch(`/api/book-club/duty-days/${day.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || "保存失败");
        return;
      }
      setWeekDetail((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          days: (prev.days || []).map((d) => (d.id === day.id ? data.item : d)),
        };
      });
      setMessage("已保存");
    } finally {
      setSavingId(null);
    }
  };

  if (!current?.id) return null;

  return (
    <section className="bg-white rounded-2xl shadow-lg overflow-hidden border border-amber-100">
      <div className="bg-gradient-to-r from-amber-500 to-orange-400 px-6 py-3 text-white">
        <h2 className="font-bold">学生值日表</h2>
        <p className="text-xs text-amber-50 mt-0.5">
          周一至周五 {current.schedule_note || "07:00 - 08:00"} · 无需登录，直接填写主持 / 唱歌 / 放映
        </p>
      </div>

      <div className="p-4 md:p-6 space-y-4">
        {loading ? (
          <p className="text-center text-gray-400 py-8">加载值日表...</p>
        ) : weeks.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm space-y-2">
            <p>本期值日表尚未创建</p>
            <p className="text-xs text-gray-400">管理员可在后台「读书会管理」中按周次添加</p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              {weeks.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => setActiveWeekId(w.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition ${
                    activeWeekId === w.id
                      ? "bg-amber-500 text-white border-amber-500"
                      : "bg-amber-50 text-amber-800 border-amber-100 hover:border-amber-300"
                  }`}
                >
                  {w.week_label}
                </button>
              ))}
            </div>

            {weekDetail && (
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full min-w-[640px] text-sm text-center border-collapse">
                  <thead>
                    <tr>
                      <th
                        colSpan={2}
                        className="border border-gray-300 bg-amber-200 px-2 py-2 font-semibold text-gray-800"
                      >
                        学生值日表
                      </th>
                      <th
                        colSpan={3}
                        className="border border-gray-300 bg-orange-300 px-2 py-2 font-semibold text-gray-800"
                      >
                        岗位安排
                      </th>
                      <th className="border border-gray-300 bg-rose-400 px-2 py-2 font-semibold text-white">
                        读书计划
                      </th>
                      <th className="border border-gray-300 bg-gray-100 px-2 py-2 text-xs text-gray-500 font-medium">
                        操作
                      </th>
                    </tr>
                    <tr>
                      <th className="border border-gray-300 bg-amber-100 px-2 py-1.5 text-gray-700">
                        日期
                      </th>
                      <th className="border border-gray-300 bg-amber-100 px-2 py-1.5 text-gray-700">
                        礼拜
                      </th>
                      {ROLE_FIELDS.map((role) => (
                        <th
                          key={role.key}
                          className={`border border-gray-300 px-2 py-1.5 text-gray-700 ${role.headerBg}`}
                        >
                          {role.label}
                        </th>
                      ))}
                      <th className="border border-gray-300 bg-rose-100 px-2 py-1.5 text-gray-700">
                        标题（内容）
                      </th>
                      <th className="border border-gray-300 bg-gray-50 px-2 py-1.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {(weekDetail.days || []).map((day) => {
                      const rest = day.is_rest === 1;
                      return (
                        <tr key={day.id}>
                          <td className="border border-gray-300 bg-amber-50 px-1 py-1 font-medium text-gray-700">
                            {day.date_label}
                          </td>
                          <td className="border border-gray-300 bg-amber-50 px-1 py-1 text-gray-700">
                            {day.weekday_label}
                          </td>
                          {ROLE_FIELDS.map((role) => (
                            <td
                              key={role.key}
                              className={`border border-gray-300 px-1 py-1 ${role.cellBg}`}
                            >
                              {rest ? (
                                <span className="text-gray-400">/</span>
                              ) : (
                                <input
                                  value={day[role.key] || ""}
                                  onChange={(e) =>
                                    updateDayField(day.id, role.key, e.target.value)
                                  }
                                  placeholder="填写"
                                  className="w-full min-w-[4.5rem] bg-transparent text-center outline-none focus:bg-white/80 rounded px-1 py-0.5"
                                />
                              )}
                            </td>
                          ))}
                          <td className="border border-gray-300 bg-rose-50 px-1 py-1">
                            {rest ? (
                              <span className="text-gray-500">休</span>
                            ) : (
                              <input
                                value={day.reading_content || ""}
                                onChange={(e) =>
                                  updateDayField(day.id, "reading_content", e.target.value)
                                }
                                placeholder="如：卷三 (1-4)"
                                className="w-full min-w-[7rem] bg-transparent text-center outline-none focus:bg-white/80 rounded px-1 py-0.5"
                              />
                            )}
                          </td>
                          <td className="border border-gray-300 bg-white px-1 py-1">
                            {rest ? (
                              <span className="text-gray-300 text-xs">—</span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => saveDay(day)}
                                disabled={savingId === day.id}
                                className="text-xs px-2 py-1 rounded bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-60"
                              >
                                {savingId === day.id ? "..." : "保存"}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {message && (
              <p className="text-center text-sm text-emerald-600">{message}</p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
